import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface ThemeConfig {
  name: string;
  displayName: string;
  colors: {
    primary: string;
    primaryHover: string;
    primaryLight: string;
    primaryLighter: string;
    primaryLightest: string;
    primaryShadow: string;
    gradientStart: string;
    gradientEnd: string;
    accent?: string;
    secondary?: string;
    success?: string;
    warning?: string;
    error?: string;
    info?: string;
  };
  assets: {
    logo: string;
    logoSmall: string;
    favicon: string;
    loginBackground?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private readonly DEFAULT_THEME: ThemeConfig = {
    name: 'default',
    displayName: 'Stratocyte',
    colors: {
      primary: '#584ca0',
      primaryHover: '#42368a',
      primaryLight: '#e6e2ff',
      primaryLighter: '#f0edff',
      primaryLightest: '#f8f6ff',
      primaryShadow: 'rgba(88, 76, 160, 0.25)',
      gradientStart: '#584ca0',
      gradientEnd: '#7969dd',
      success: '#29a643',
      warning: '#f2a900',
      error: '#f34436',
      info: '#00bcd4'
    },
    assets: {
      logo: 'assets/img/logo/stratocyte-logo.png',
      logoSmall: 'assets/img/logo/dashboard-logo.webp',
      favicon: 'assets/img/logo/favicon.ico',
      loginBackground: 'assets/img/logo/Stratocyte-logo.jpg'
    }
  };

  private readonly MISSION_THEME: ThemeConfig = {
    name: 'mission',
    displayName: 'Mission Pet Health',
    colors: {
      primary: '#13294b',       // Navy
      primaryHover: '#0a1a2e',  // Darker Navy
      primaryLight: '#d6e4f0',  // Light Navy
      primaryLighter: '#e8f0f7', // Very Light Navy
      primaryLightest: '#f2f6fa', // Lightest Navy
      primaryShadow: 'rgba(19, 41, 75, 0.25)', // Shadow for focus states
      gradientStart: '#13294b',
      gradientEnd: '#1e3a5f',
      accent: '#9abe26',        // Lime Green
      secondary: '#00afd4',     // Cyan
      success: '#9abe26',       // Use accent for success
      warning: '#f2a900',       // Amber
      error: '#b12373',         // Magenta
      info: '#00afd4'           // Cyan
    },
    assets: {
      logo: 'assets/img/logo/mph-logo.png',
      logoSmall: 'assets/img/logo/mph-logo.png',
      favicon: 'assets/img/logo/mph-favicon.ico',
      loginBackground: 'assets/img/logo/Mission Pet Health Logo.jpg'
    }
  };

  private currentTheme: ThemeConfig = this.DEFAULT_THEME;

  constructor() { }

  /**
   * Initialize theme based on environment configuration
   */
  initializeTheme(): void {
    const themeName = environment.features?.theme || 'default';
    this.applyTheme(themeName);
  }

  /**
   * Apply theme by name
   */
  applyTheme(themeName: string): void {
    let theme: ThemeConfig;

    switch (themeName.toLowerCase()) {
      case 'mission':
        theme = this.MISSION_THEME;
        break;
      case 'default':
      default:
        theme = this.DEFAULT_THEME;
        break;
    }

    this.setTheme(theme);
  }

  /**
   * Apply custom theme configuration
   */
  setTheme(theme: ThemeConfig): void {
    this.currentTheme = theme;

    const root = document.documentElement;

    // Apply all color variables to CSS custom properties
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-primary-hover', theme.colors.primaryHover);
    root.style.setProperty('--color-primary-light', theme.colors.primaryLight);
    root.style.setProperty('--color-primary-lighter', theme.colors.primaryLighter);
    root.style.setProperty('--color-primary-lightest', theme.colors.primaryLightest);
    root.style.setProperty('--color-primary-shadow', theme.colors.primaryShadow);
    root.style.setProperty('--gradient-start', theme.colors.gradientStart);
    root.style.setProperty('--gradient-end', theme.colors.gradientEnd);

    if (theme.colors.accent) {
      root.style.setProperty('--color-accent', theme.colors.accent);
    }

    if (theme.colors.secondary) {
      root.style.setProperty('--color-secondary', theme.colors.secondary);
    }

    if (theme.colors.success) {
      root.style.setProperty('--color-success', theme.colors.success);
    }

    if (theme.colors.warning) {
      root.style.setProperty('--color-warning', theme.colors.warning);
    }

    if (theme.colors.error) {
      root.style.setProperty('--color-error', theme.colors.error);
    }

    if (theme.colors.info) {
      root.style.setProperty('--color-info', theme.colors.info);
    }

    // Add theme class to body for additional styling hooks
    document.body.classList.remove('theme-default', 'theme-mission');
    document.body.classList.add(`theme-${theme.name}`);

    // Update favicon when theme changes
    this.updateFavicon();
  }

  /**
   * Get current theme configuration
   */
  getCurrentTheme(): ThemeConfig {
    return this.currentTheme;
  }

  /**
   * Get theme name from current theme
   */
  getCurrentThemeName(): string {
    return this.currentTheme.name;
  }

  /**
   * Check if Mission theme is active
   */
  isMissionTheme(): boolean {
    return this.currentTheme.name === 'mission';
  }

  /**
   * Check if default theme is active
   */
  isDefaultTheme(): boolean {
    return this.currentTheme.name === 'default';
  }

  /**
   * Get primary color for current theme
   */
  getPrimaryColor(): string {
    return this.currentTheme.colors.primary;
  }

  /**
   * Get accent color for current theme
   */
  getAccentColor(): string {
    return this.currentTheme.colors.accent || this.currentTheme.colors.primary;
  }

  /**
   * Reset to default theme
   */
  resetToDefault(): void {
    this.setTheme(this.DEFAULT_THEME);
  }

  /**
   * Get logo path for current theme
   */
  getLogoPath(): string {
    return this.currentTheme.assets.logo;
  }

  /**
   * Get small logo path for current theme
   */
  getSmallLogoPath(): string {
    return this.currentTheme.assets.logoSmall;
  }

  /**
   * Get favicon path for current theme
   */
  getFaviconPath(): string {
    return this.currentTheme.assets.favicon;
  }

  /**
   * Get login background path for current theme
   */
  getLoginBackgroundPath(): string | undefined {
    return this.currentTheme.assets.loginBackground;
  }

  /**
   * Get display name for current theme
   */
  getDisplayName(): string {
    return this.currentTheme.displayName;
  }

  /**
   * Update favicon dynamically based on current theme
   */
  updateFavicon(): void {
    const faviconPath = this.getFaviconPath();
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconPath;

    if (!document.querySelector("link[rel*='icon']")) {
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }
}
